'use client'

import { ReactNode, MouseEvent } from 'react'
import classNames from 'classnames'
import { Badge, Cell, Image, Spacer, Text, Icon } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import { getTestId } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { ItemBoxModel } from '@marketplace-web/item-box/product-item-data'
import { ItemBoxPricing } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'
import {
  shouldTrackOncePerSessionDay,
  useAbTest,
  useTrackAbTestCallback,
  AbTestDto,
} from '@marketplace-web/feature-flags/ab-tests-data'
import { ProactiveModerationItemCheckbox } from '@marketplace-web/proactive-moderation/proactive-moderation-feature'

import ItemBoxFavourite from './ItemBoxFavourite'
import ItemBoxMessage from './ItemBoxMessage'
import ItemBoxFavouriteIcon from './ItemBoxFavouriteIcon'
import InformationBreakdown from './InformationBreakdown'
import { getIconName } from './utils/getIconName'

export type RenderFavouriteArgsType = {
  favourite: React.ComponentProps<typeof ItemBoxFavourite>
}

type Props = {
  itemId: number
  price: string
  favourite?: React.ComponentProps<typeof ItemBoxFavourite> | null
  oldPrice?: string | null
  url?: string
  badge?: React.ComponentProps<typeof Badge> | null
  image?: string | null
  /**
   * Sets the background color on an image element.
   * Accepts any legal CSS color values (Hexadecimal, RGB, predefined names etc.).
   */
  imageColor?: string | null
  imagesExperimental?: Array<string>
  imagesExperimentalOverlayDisabled?: boolean
  /**
   * Accepts custom favourite element.
   * `favourite` has to be defined for this property to take effect.
   */

  renderFooter?: ReactNode
  renderPriceBreakdown?: ReactNode
  onClick?: (event: MouseEvent) => void
  status?: React.ComponentProps<typeof ItemBoxMessage> | null
  bumpText?: ReactNode
  /**
   * Adds data-testid attribute to parent and children components.
   * When used, --badge, --selected-icon, --image, --overlay-link,
   * --price-text, --old-price-text, --bump-text,
   * --footer, --favourite, --owner, --description, --status suffixes applied accordingly.
   */
  testId?: string
  secondaryBadgeExperimental?: React.ComponentProps<typeof Badge> | null
  hasFavouritedChanged?: boolean
  itemBox?: ItemBoxModel | null
  openItemInNewTab?: boolean
}

const MIN_COLLAGE_IMAGES_COUNT = 3

const ItemBox = ({
  itemId,
  price,
  favourite,
  oldPrice,
  url,
  badge,
  image,
  imageColor,
  imagesExperimental,
  imagesExperimentalOverlayDisabled = false,
  renderFooter,
  renderPriceBreakdown,
  onClick,
  status,
  bumpText,
  testId,
  secondaryBadgeExperimental,
  hasFavouritedChanged,
  itemBox,
  openItemInNewTab = false,
}: Props) => {
  const itemBoxAspectRatioChangeAbTest = useAbTest('item_box_aspect_ratio_change')
  const itemBoxSummarySpacingRemovalAbTest = useAbTest('item_box_summary_spacing_removal')
  const trackExpose = useTrackAbTestCallback()

  const itemBoxSummarySpacingRemovalAbTestEnabled =
    itemBoxSummarySpacingRemovalAbTest?.variant === 'on'

  const handleExposeEvent = (abTest: AbTestDto | undefined) => (inView: boolean) => {
    if (!inView) return
    if (!abTest) return

    trackExpose(abTest, shouldTrackOncePerSessionDay)
  }

  const renderFavouriteIcon = () => {
    if (!favourite) return null

    return (
      <ItemBoxFavouriteIcon
        {...favourite}
        hasFavouritedChanged={hasFavouritedChanged}
        hasOverlay={!!status}
        testId={testId}
      />
    )
  }

  const renderBadges = () => {
    const iconName = getIconName(itemBox?.badge?.icon)
    const secondaryBadge = itemBox?.badge
      ? {
          content: itemBox.badge.title,
          icon: iconName ? <Icon name={iconName} color={itemBox.badge.style} /> : undefined,
        }
      : secondaryBadgeExperimental

    return (
      <div data-testid={getTestId(testId, 'badge-container')}>
        <Cell styling={Cell.Styling.Narrow} theme="transparent">
          <div className="u-flexbox u-flex-direction-column u-align-items-flex-start u-gap-small">
            {badge ? <Badge testId={getTestId(testId, 'badge')} {...badge} /> : null}
            {secondaryBadge ? (
              <Badge testId={getTestId(testId, 'secondary-badge')} {...secondaryBadge} />
            ) : null}
          </div>
        </Cell>
      </div>
    )
  }

  const renderOverlay = () => {
    const showBadges = itemBox?.badge || badge || secondaryBadgeExperimental

    return (
      <>
        <div>{showBadges && renderBadges()}</div>
        {status ? <ItemBoxMessage testId={testId} {...status} /> : null}
      </>
    )
  }

  const renderExperimentalImagesCollage = () => {
    if (!imagesExperimental || imagesExperimental.length < MIN_COLLAGE_IMAGES_COUNT) return null

    const showAdditionalImagesOverlay =
      imagesExperimental.length > MIN_COLLAGE_IMAGES_COUNT && !imagesExperimentalOverlayDisabled
    const additionalImagesCount = imagesExperimental.length - MIN_COLLAGE_IMAGES_COUNT + 1

    return (
      <div className="new-item-box__collage" data-testid={getTestId(testId, 'collage')}>
        <div className="new-item-box__image collage--image-1">
          <Image
            src={imagesExperimental[0]}
            scaling={Image.Scaling.Cover}
            ratio={Image.Ratio.Portrait}
            color={imageColor}
            alt={itemBox?.accessibilityLabel}
            testId={getTestId(testId, 'image-1')}
          />
        </div>
        <div className="new-item-box__image collage--image-2">
          <Image
            src={imagesExperimental[1]}
            scaling={Image.Scaling.Cover}
            ratio={Image.Ratio.Portrait}
            color={imageColor}
            alt={itemBox?.accessibilityLabel}
            testId={getTestId(testId, 'image-2')}
          />
        </div>
        <div className="new-item-box__image collage--image-3">
          <Image
            src={imagesExperimental[2]}
            scaling={Image.Scaling.Cover}
            ratio={Image.Ratio.Portrait}
            color={imageColor}
            alt={itemBox?.accessibilityLabel}
            testId={getTestId(testId, 'image-3')}
          />
          {showAdditionalImagesOverlay ? (
            <div className="new-item-box__image-overlay">
              <Text
                type={Text.Type.Heading}
                theme="inverse"
                text={`+${additionalImagesCount}`}
                as="span"
              />
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  const renderImage = () => {
    const itemBoxAspectRatioChangeAbTestEnabled = itemBoxAspectRatioChangeAbTest?.variant === 'on'

    return (
      <InView
        className="new-item-box__image"
        onChange={handleExposeEvent(itemBoxAspectRatioChangeAbTest)}
      >
        <Image
          src={image}
          scaling={Image.Scaling.Cover}
          ratio={
            itemBoxAspectRatioChangeAbTestEnabled ? Image.Ratio.SmallPortrait : Image.Ratio.Portrait
          }
          color={imageColor}
          alt={itemBox?.accessibilityLabel}
          testId={getTestId(testId, 'image')}
          styling={Image.Styling.Rounded}
        />
      </InView>
    )
  }

  const itemBoxOverlayClasses = classNames('new-item-box__overlay', {
    'new-item-box__overlay--clickable': !!onClick || !!url,
  })

  return (
    <div className="new-item-box__container" data-testid={testId}>
      <div className="u-position-relative u-min-height-none u-flex-auto new-item-box__image-container">
        <ProactiveModerationItemCheckbox itemId={itemId} />
        {renderExperimentalImagesCollage() || renderImage()}
        {renderFavouriteIcon()}
        {url ? (
          <a
            href={url}
            className={itemBoxOverlayClasses}
            onClick={onClick}
            data-testid={getTestId(testId, 'overlay-link')}
            title={itemBox?.accessibilityLabel}
            target={openItemInNewTab ? '_blank' : '_self'}
            rel="noreferrer"
          >
            {renderOverlay()}
          </a>
        ) : (
          <div className={itemBoxOverlayClasses} onClick={onClick} aria-hidden="true">
            {renderOverlay()}
          </div>
        )}
      </div>

      <InView
        as="div"
        className={classNames({
          'new-item-box__summary': itemBoxSummarySpacingRemovalAbTestEnabled,
        })}
        onChange={handleExposeEvent(itemBoxSummarySpacingRemovalAbTest)}
      >
        <Cell
          styling={
            itemBoxSummarySpacingRemovalAbTestEnabled ? Cell.Styling.Tight : Cell.Styling.Narrow
          }
        >
          <InformationBreakdown
            description={{
              title: itemBox?.firstLine || '',
              subtitle: itemBox?.secondLine || '',
              exposure: itemBox?.exposure,
              exposures: itemBox?.exposures,
            }}
            testId={testId}
            renderPriceBreakdown={renderPriceBreakdown}
            renderPrice={<ItemBoxPricing price={price} oldPrice={oldPrice} testId={testId} />}
            bumpText={bumpText}
            actions={itemBox?.actions}
            itemId={itemBox?.itemId}
          />
          {renderFooter && (
            <div data-testid={getTestId(testId, 'footer')}>
              <Spacer />
              {renderFooter}
            </div>
          )}
        </Cell>
      </InView>
    </div>
  )
}

export default ItemBox
